// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { RootState } from 'admin/core/store';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import * as storageActions from 'admin/storage/actions';
import { connect } from 'react-redux';
import {
    RouteComponentProps,
    withRouter,
} from 'react-router';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import { Loader } from 'common/components';
import { WithErrorPages } from 'common/components/WithPageNotFound/WithErrorPages';
import {
    Action,
    Icon,
    Translate,
} from '@plesk/ui-library';
import { Link } from 'react-router-dom';
import { pathTo } from 'common/helpers/core';
import PageHeader from 'common/components/PageHeader/PageHeader';
import {
    PageSection,
    SubTitleRow,
    SubTitleSection,
    SubTitleSectionLabel,
} from 'common/components/PageHeader/Styles';
import { ICONS } from 'common/constants';
import { STORAGE_TYPES_TRANSLATION_MAP } from 'common/api/resources/StorageType';
import { IFailoverDomainResponse } from 'common/api/resources/FailoverDomain';
import FailoverDomainTable from 'admin/failoverDomain/containers/FailoverDomainTable';
import { StyledTable } from 'common/components/styles/StyledTable';
import useQuery from 'common/hooks/useQuery';
import { useRequestCancellationEffect } from 'common/hooks/useRequestCancellationEffect';
import { reloadListData } from 'common/helpers/list';

export type StorageFailoverDomainsPageProps =
    RouteComponentProps<{ id: string }> &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

export const StorageFailoverDomainsPage: React.FC<StorageFailoverDomainsPageProps> = ({
    storage,
    failoverDomains,
    isLoading,
    isLoadingFailoverDomains,
    storageActions: {
        getStorage,
        getStorageFailoverDomains,
        toggleStorageFailoverDomainActive,
        deleteStorageFailoverDomain,
    },
    match: { params },
}) => {
    const storageId = parseInt(params.id, 10);
    const [urlParams] = useQuery<{ is_active?: string }>();

    const filters = urlParams.is_active !== undefined ? { is_active: urlParams.is_active } : undefined;

    const onMount = async () => await getStorage(storageId);

    const loadPaginated = (page: number) => getStorageFailoverDomains(storageId, { page, filters });

    const isFirstLoading = useRequestCancellationEffect(
        token => getStorageFailoverDomains(storageId, { filters }, token),
        [storageId, urlParams.is_active]
    );

    const handleToggleActive = (item: IFailoverDomainResponse) => {
        toggleStorageFailoverDomainActive(item.id, { is_active: !item.is_active });
    };

    const handleDelete = async (item: IFailoverDomainResponse) => {
        if (await deleteStorageFailoverDomain(item.id)) {
            reloadListData(failoverDomains, loadPaginated);
        }
    };

    const renderTitle = () => (
        <PageSection>
            <Action component={Link} to={pathTo('storage')}>
                <Translate content="storage.page.breadcrumb" />
            </Action>
            <Icon name={ICONS.CHEVRON_RIGHT} />
            <Action component={Link} to={pathTo(`storage/${storageId}`)}>
                {storage.name}
            </Action>
            <Icon name={ICONS.CHEVRON_RIGHT} />
            <Translate content="storage.page.stats.failoverDomains" />
        </PageSection>
    );

    const renderSubtitle = () => (
        <SubTitleRow>
            <SubTitleSection>
                <SubTitleSectionLabel>
                    <Translate content="storage.page.type" />
                </SubTitleSectionLabel>
                {STORAGE_TYPES_TRANSLATION_MAP[storage.type.name]}
            </SubTitleSection>
            {storage.storage_tag && (
                <SubTitleSection>
                    <SubTitleSectionLabel>
                        <Translate content="storage.page.storageTier" />
                    </SubTitleSectionLabel>
                    {storage.storage_tag.name}
                </SubTitleSection>
            )}
        </SubTitleRow>
    );

    return (
        <WithErrorPages onMount={onMount} deps={[storageId]}>
            <Loader isLoading={isLoading}>
                <PageHeader
                    title={renderTitle()}
                    subtitle={renderSubtitle()}
                />
                <StyledTable>
                    <Loader isLoading={isFirstLoading}>
                        <FailoverDomainTable
                            items={failoverDomains.data}
                            meta={failoverDomains.meta}
                            loadItems={loadPaginated}
                            isLoading={isLoadingFailoverDomains}
                            isFirstLoading={isFirstLoading}
                            onCreate={() => undefined}
                            onEdit={() => undefined}
                            onToggleActive={handleToggleActive}
                            onDelete={handleDelete}
                        />
                    </Loader>
                </StyledTable>
            </Loader>
        </WithErrorPages>
    );
};

const mapStateToProps = (state: RootState) => ({
    storage: state.storage.item,
    failoverDomains: state.storage.failoverDomains,
    isLoading: state.app.loadingFlags.has(LOADING_FLAGS.STORAGE_GET),
    isLoadingFailoverDomains: state.app.loadingFlags.has(LOADING_FLAGS.STORAGE_FAILOVER_DOMAIN_LIST),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    storageActions: bindActionCreators(storageActions, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(StorageFailoverDomainsPage));
